/**
 * CustomFaixaVencidas — o que venceu e continua aberto (ficha #27, Fase 3).
 *
 * ─── O PROBLEMA QUE ELA RESOLVE, E QUE O CALENDÁRIO NÃO RESOLVE ────────────
 *
 * O evento que passou acabou, e fica no dia dele — certo. **A tarefa vencida
 * não.** O alvará que venceu dia 31 e ninguém pagou continua pendente, mas no
 * calendário ele fica parado no dia 31, onde ninguém mais olha. É por isso que
 * todo app de tarefa tem lista E calendário, e foi o recado que a ficha #32
 * deixou aqui em 15/09.
 *
 * A resposta é esta faixa: ela vem de `/agenda/entradas` **fora da janela** que
 * o calendário está mostrando, e fica ACIMA dele. Se ficasse dentro do mês,
 * repetiria o defeito — o lugar delas no tempo é justamente o problema.
 *
 * ─── POR QUE NÃO É UM ALERTA VERMELHO ──────────────────────────────────────
 *
 * Porque vermelho constante deixa de ser aviso. A faixa é discreta e some
 * sozinha quando não há nada vencido — que é o estado normal. Quando aparece,
 * aparece com número e com nome, que é o que faz alguém agir.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '@/lib/api';
import { formatarData } from '@/lib/data';

export interface EntradaVencida {
  id: string;
  origem: string;
  origem_id: number;
  titulo: string;
  data: string | null;
  responsavel: string | null;
  adiamentos: number;
  link: string;
}

interface Props {
  /** O período que o calendário mostra — só para recarregar junto com ele. */
  de: string;
  ate: string;
}

export function CustomFaixaVencidas({ de, ate }: Props) {
  const [vencidas, setVencidas] = useState<EntradaVencida[]>([]);
  const [aberta, setAberta] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await api.get<{ vencidas: EntradaVencida[] }>(
          `/agenda/entradas?de=${de}&ate=${ate}`,
        );
        if (vivo) setVencidas(r.vencidas ?? []);
      } catch {
        // A faixa é informação a mais: se a porta falhar, a agenda continua
        // inteira. Errar para menos aqui é melhor que uma tela de erro por
        // causa de um aviso.
        if (vivo) setVencidas([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [de, ate]);

  if (vencidas.length === 0) return null;

  return (
    <div className="alert alert-warning py-2 mb-3">
      <div className="d-flex align-items-center justify-content-between">
        <span className="fw-medium">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {vencidas.length} {vencidas.length === 1 ? 'tarefa venceu' : 'tarefas venceram'} e
          {vencidas.length === 1 ? ' continua' : ' continuam'} em aberto
        </span>
        <button
          type="button"
          className="btn btn-sm btn-link"
          onClick={() => setAberta((a) => !a)}
        >
          {aberta ? 'Esconder' : 'Ver quais'}
        </button>
      </div>

      {aberta && (
        <ul className="list-unstyled mb-0 mt-2 small">
          {vencidas.map((v) => (
            <li key={v.id} className="py-1 border-top">
              <Link to={v.link} className="fw-medium">
                {v.titulo}
              </Link>
              <span className="text-secondary">
                {' '}
                · venceu em {formatarData(v.data)}
                {v.responsavel ? ` · ${v.responsavel}` : ' · sem responsável'}
                {/* Três adiamentos é informação: a tarefa que só anda de semana
                    em semana é um problema diferente do "esqueceram dela". */}
                {v.adiamentos > 0 ? ` · adiada ${v.adiamentos}×` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CustomFaixaVencidas;
